import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";

const sections = [
  {
    title: "1. Présentation de l'application",
    body: (
      <p>
        Lift AI est une application mobile permettant aux utilisateurs
        d'obtenir des analyses physiques et des recommandations personnalisées
        grâce à l'intelligence artificielle.
      </p>
    ),
  },
  {
    title: "2. Compte utilisateur",
    body: (
      <>
        <p>
          Certaines fonctionnalités peuvent nécessiter la création d'un compte
          via Apple ou Google.
        </p>
        <p>
          L'utilisateur est responsable des informations qu'il transmet et de
          l'utilisation de son compte.
        </p>
      </>
    ),
  },
  {
    title: "3. Utilisation de l'application",
    body: (
      <>
        <p>
          L'utilisateur s'engage à utiliser Lift AI de manière légale et
          respectueuse.
        </p>
        <p>Il est interdit :</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>d'utiliser l'application à des fins frauduleuses,</li>
          <li>de perturber le fonctionnement du service,</li>
          <li>de transmettre du contenu illégal, offensant ou inapproprié.</li>
        </ul>
        <p>
          Lift AI se réserve le droit de suspendre ou supprimer un compte en
          cas d'abus.
        </p>
      </>
    ),
  },
  {
    title: "4. Abonnements",
    body: (
      <>
        <p>
          L'accès complet à l'application est accessible via un paiement ou un
          abonnement payant.
        </p>
        <p>
          Les paiements et abonnements sont gérés par Apple via l'App Store et
          sont soumis aux conditions d'Apple.
        </p>
        <p>
          Les abonnements peuvent être annulés à tout moment depuis les
          réglages du compte Apple de l'utilisateur.
        </p>
      </>
    ),
  },
  {
    title: "5. Limitation de responsabilité",
    body: (
      <>
        <p>
          Lift AI fournit des analyses et recommandations générées par
          intelligence artificielle à titre informatif uniquement.
        </p>
        <p>
          L'application ne remplace pas un professionnel de santé, un médecin
          ou un coach certifié.
        </p>
        <p>
          L'utilisateur reste seul responsable de l'utilisation des
          informations fournies par l'application.
        </p>
      </>
    ),
  },
  {
    title: "6. Disponibilité du service",
    body: (
      <>
        <p>
          Lift AI peut être modifiée, suspendue ou interrompue à tout moment
          sans préavis.
        </p>
        <p>
          Nous ne garantissons pas un fonctionnement sans interruption ni
          erreur.
        </p>
      </>
    ),
  },
  {
    title: "7. Données personnelles",
    body: (
      <p>
        L'utilisation de Lift AI implique la collecte et le traitement de
        certaines données personnelles conformément à la{" "}
        <Link
          to="/politique-de-confidentialite"
          className="text-violet-600 hover:underline"
        >
          Politique de confidentialité
        </Link>{" "}
        de l'application.
      </p>
    ),
  },
  {
    title: "8. Modification des conditions",
    body: (
      <>
        <p>
          Les présentes Conditions d'Utilisation peuvent être modifiées à tout
          moment.
        </p>
        <p>
          La poursuite de l'utilisation de Lift AI après modification vaut
          acceptation des nouvelles conditions.
        </p>
      </>
    ),
  },
  {
    title: "9. Contact",
    body: (
      <p>
        Pour toute question concernant Lift AI :{" "}
        <a
          href="mailto:contact@liftai.fr"
          className="text-violet-600 hover:underline"
        >
          contact@liftai.fr
        </a>
      </p>
    ),
  },
];

export default function TermsOfUse() {
  return (
    <main
      data-testid="terms-of-use-page"
      className="bg-white text-neutral-950 font-[Instrument_Sans]"
    >
      <Header />

      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 px-6 lg:px-10 border-b border-neutral-200">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-violet-600 transition-colors mb-8 group"
          >
            <ArrowLeft
              size={14}
              className="group-hover:-translate-x-0.5 transition-transform"
            />
            Retour à l'accueil
          </Link>

          <p className="text-sm font-medium text-violet-600 mb-4 uppercase tracking-wider">
            Légal
          </p>
          <h1
            data-testid="terms-page-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-neutral-950 mb-6"
          >
            Conditions Générales d'Utilisation
          </h1>
          <p className="text-base text-neutral-500">
            Dernière mise à jour : Mai 2026
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4 text-lg text-neutral-700 leading-relaxed mb-16">
            <p>
              Les présentes Conditions Générales d'Utilisation régissent
              l'utilisation de l'application Lift AI éditée par Pulsar
              Plateformes.
            </p>
            <p>En utilisant Lift AI, vous acceptez les présentes conditions.</p>
          </div>

          <div className="space-y-14">
            {sections.map((s, i) => (
              <article
                key={s.title}
                data-testid={`terms-section-${i}`}
                className="scroll-mt-24"
              >
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-neutral-950 mb-5">
                  {s.title}
                </h2>
                <div className="space-y-4 text-base text-neutral-700 leading-relaxed">
                  {s.body}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
